import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="country-selector"
export default class extends Controller {
    static targets = [
        "dropdown", "selector", "flag", "code", "search", "list",
        "spinner", "loadingState", "emptyState", "hiddenField", "chevron"
    ]

    static values = {
        initialValue: String,
        detectIp: { type: Boolean, default: true } // Set to false to disable IP detection
    }

    connect() {
        this.countries = []
        this.searchTimeout = null
        this.debounceDelay = 300

        // ---- INITIAL VALUE HANDLING ------------------------------------
        if (this.hasInitialValueValue && this.initialValueValue) {
            // If server provided a value → fetch country info from server
            this.fetchAndApplyCountryByCode(this.initialValueValue)
        } else if (this.detectIpValue) {
            // Detect country from IP (for signup forms)
            this.detectCountryFromIP()
        } else {
            // Show empty/placeholder state (for linking demands form)
            this.showEmptyState()
        }
        // -----------------------------------------------------------------

        document.addEventListener("click", this.closeOnOutsideClick)
        document.addEventListener("keydown", this.keyboardNavigation)
    }

    disconnect() {
        document.removeEventListener("click", this.closeOnOutsideClick)
        document.removeEventListener("keydown", this.keyboardNavigation)
    }

    // --- EMPTY STATE ----------------------------------------------------

    showEmptyState() {
        if (!this.hasFlagTarget || !this.hasCodeTarget || !this.hasHiddenFieldTarget) return

        // Set placeholder/empty state
        this.flagTarget.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='15' viewBox='0 0 20 15'%3E%3Crect fill='%23e2e8f0' width='20' height='15' rx='2'/%3E%3C/svg%3E"
        this.flagTarget.alt = "Select country"
        
        // Show placeholder text
        const placeholder = this.codeTarget.dataset.placeholder || "Indicatif"
        this.codeTarget.textContent = placeholder
        this.codeTarget.classList.add("text-slate-400", "dark:text-slate-500")
        this.codeTarget.classList.remove("text-slate-900", "dark:text-white")
        
        this.hiddenFieldTarget.value = ""
        
        // Dispatch change event for validation
        this.hiddenFieldTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }

    // --- IP DETECTION ---------------------------------------------------

    async detectCountryFromIP() {
        // Default fallback country
        const defaultCountry = {
            name: "France",
            code: "33",
            flag: "https://flagcdn.com/w20/fr.png"
        }

        // Check cache first (24 hour expiration)
        const cacheKey = "ipapi_country_detection"
        const cacheExpiry = 24 * 60 * 60 * 1000 // 24 hours in milliseconds

        try {
            const cached = localStorage.getItem(cacheKey)
            if (cached) {
                const cacheData = JSON.parse(cached)
                const now = Date.now()

                // Check if cache is still valid
                if (cacheData.timestamp && (now - cacheData.timestamp) < cacheExpiry) {
                    this.applyCountryToUI({
                        name: cacheData.country.name,
                        code: cacheData.country.code,
                        flag: cacheData.country.flag
                    })
                    return
                } else {
                    // Cache expired, remove it
                    localStorage.removeItem(cacheKey)
                }
            }
        } catch (e) {
            // If cache is corrupted, remove it and continue
            localStorage.removeItem(cacheKey)
        }

        // Cache miss or expired - fetch from API
        try {
            const response = await fetch("https://ipapi.co/json/")

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }
            
            const json = await response.json()

            if (json.country && json.country_calling_code) {
                const countryData = {
                    name: json.country_name,
                    code: json.country_calling_code.replace("+", ""),
                    flag: `https://flagcdn.com/w20/${json.country.toLowerCase()}.png`
                }

                // Store in cache with timestamp
                try {
                    localStorage.setItem(cacheKey, JSON.stringify({
                        country: countryData,
                        timestamp: Date.now()
                    }))
                } catch (e) {
                    // localStorage might be disabled or full, continue anyway
                    console.warn("Failed to cache IP detection:", e)
                }

                this.applyCountryToUI(countryData)
                return
            }
        } catch (e) {
            console.warn("IP country detection failed:", e)

            // If API call fails and we have expired cache, use it as fallback
            try {
                const cached = localStorage.getItem(cacheKey)
                if (cached) {
                    const cacheData = JSON.parse(cached)
                    if (cacheData.country) {
                        this.applyCountryToUI(cacheData.country)
                        return
                    }
                }
            } catch (e) {
                // Ignore cache errors
            }
        }

        // Fallback if detection failed
        this.applyCountryToUI(defaultCountry)
    }

    // --- FETCH COUNTRY BY CODE ------------------------------------------

    async fetchAndApplyCountryByCode(code) {
        if (!code || String(code).trim() === "") {
            this.showEmptyState()
            return
        }

        // Remove "+" prefix if present for API call
        const cleanCode = String(code).replace(/^\+/, "").trim()

        try {
            const response = await fetch(`/countries/find_by_code?code=${encodeURIComponent(cleanCode)}&with_flag=true`, {
                headers: { "Accept": "application/json" }
            })

            if (response.ok) {
                const country = await response.json()
                this.applyCountryToUI({
                    name: country.name,
                    code: String(country.code).replace(/^\+/, ""),
                    flag: country.flag
                })
            } else {
                // Country not found - keep empty state
                console.warn(`Country not found for code: ${cleanCode}`)
                this.showEmptyState()
            }
        } catch (e) {
            console.error("Failed to fetch country by code:", e)
            this.showEmptyState()
        }
    }

    // Apply country to UI elements without closing dropdown
    applyCountryToUI(country) {
        if (!this.hasFlagTarget || !this.hasCodeTarget || !this.hasHiddenFieldTarget) return

        // Ensure code never has "+" prefix
        const cleanCode = String(country.code).replace(/^\+/, "")

        this.flagTarget.src = country.flag
        this.flagTarget.alt = `${country.name} Flag`
        this.codeTarget.textContent = cleanCode
        
        // Apply selected styling (not placeholder)
        this.codeTarget.classList.remove("text-slate-400", "dark:text-slate-500")
        this.codeTarget.classList.add("text-slate-900", "dark:text-white")
        
        this.hiddenFieldTarget.value = cleanCode
        
        // Dispatch change event for validation
        this.hiddenFieldTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }

    // --- LEGACY METHOD (for compatibility) ------------------------------

    applyInitialCountry(code) {
        // Now delegates to the async fetch method
        this.fetchAndApplyCountryByCode(code)
    }

    // --- Actions -----------------------------------------------------

    toggleDropdown(event) {
        event.stopPropagation()
        this.dropdownTarget.classList.toggle("hidden")

        // Rotate chevron icon
        if (this.hasChevronTarget) {
            this.chevronTarget.style.transform =
                this.dropdownTarget.classList.contains("hidden") ? "rotate(0deg)" : "rotate(180deg)"
        }

        if (!this.dropdownTarget.classList.contains("hidden")) {
            if (this.hasSearchTarget) this.searchTarget.focus()
            if (this.countries.length === 0) this.fetchCountries()
        }
    }

    closeOnOutsideClick = (event) => {
        if (!this.element.contains(event.target)) this.closeDropdown()
    }

    closeDropdown() {
        if (!this.hasDropdownTarget || this.dropdownTarget.classList.contains("hidden")) return

        this.dropdownTarget.classList.add("hidden")
        
        // Reset chevron rotation
        if (this.hasChevronTarget) {
            this.chevronTarget.style.transform = "rotate(0deg)"
        }
        
        if (this.hasSearchTarget) this.searchTarget.value = ""
        if (this.hasSpinnerTarget) this.spinnerTarget.classList.add("hidden")
        if (this.searchTimeout) clearTimeout(this.searchTimeout)
    }

    search() {
        if (!this.hasSearchTarget || !this.hasSpinnerTarget) return
        
        const query = this.searchTarget.value.trim()
        this.spinnerTarget.classList.remove("hidden")

        if (this.searchTimeout) clearTimeout(this.searchTimeout)

        this.searchTimeout = setTimeout(() => this.fetchCountries(query), this.debounceDelay)
    }

    // --- Keyboard navigation ----------------------------------------

    keyboardNavigation = (event) => {
        if (!this.hasDropdownTarget || this.dropdownTarget.classList.contains("hidden")) return
        if (!this.hasListTarget) return

        const items = Array.from(this.listTarget.querySelectorAll("button"))
        const focused = document.activeElement
        let index = items.indexOf(focused)

        if (event.key === "ArrowDown") {
            event.preventDefault()
            index = (index + 1) % items.length
            items[index]?.focus()
        }

        if (event.key === "ArrowUp") {
            event.preventDefault()
            index = index <= 0 ? items.length - 1 : index - 1
            items[index]?.focus()
        }

        if (event.key === "Enter" && items.includes(focused)) {
            event.preventDefault()
            focused.click()
        }

        if (event.key === "Escape") {
            this.closeDropdown()
        }
    }

    // --- Fetching countries -----------------------------------------

    async fetchCountries(searchQuery = "") {
        this.showLoadingState()

        try {
            const response = await fetch(`/countries?with_flag=true&${new URLSearchParams({ q: searchQuery })}`, {
                headers: { "Accept": "application/json" }
            })

            const json = await response.json()

            // Ensure codes never have "+" prefix
            this.countries = json.map(c => ({
                name: c.name,
                code: String(c.code).replace(/^\+/, ""),
                flag: c.flag
            }))

            this.renderCountryList()
        } catch (e) {
            console.error("Failed fetching countries", e)
            this.countries = []
            this.renderCountryList()
        }
    }

    // --- Rendering ---------------------------------------------------

    renderCountryList() {
        if (!this.hasListTarget) return
        
        this.listTarget.innerHTML = ""

        if (this.countries.length === 0) {
            if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove("hidden")
            if (this.hasLoadingStateTarget) this.loadingStateTarget.classList.add("hidden")
            if (this.hasSpinnerTarget) this.spinnerTarget.classList.add("hidden")
            return
        }

        if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
        if (this.hasLoadingStateTarget) this.loadingStateTarget.classList.add("hidden")
        if (this.hasSpinnerTarget) this.spinnerTarget.classList.add("hidden")

        this.countries.forEach(country => {
            const li = document.createElement("li")
            li.className = "country-item"

            li.innerHTML = `
                <button type="button"
                    class="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-neutral-700 focus:bg-gray-50 dark:focus:bg-neutral-700 flex items-center"
                    data-country='${JSON.stringify(country).replace(/'/g, "&#39;")}'
                >
                    <img src="${country.flag}" class="w-5 h-4 rounded mr-3">
                    <span class="text-gray-700 dark:text-gray-300 flex-1">${country.name}</span>
                    <span class="text-gray-500 dark:text-gray-400 font-medium">${country.code}</span>
                </button>
            `

            li.querySelector("button").addEventListener("click", (e) => {
                const country = JSON.parse(
                    e.currentTarget.dataset.country.replace(/&#39;/g, "'")
                )
                this.selectCountry(country)
            })

            this.listTarget.appendChild(li)
        })
    }

    showLoadingState() {
        if (this.hasListTarget) this.listTarget.innerHTML = ""
        if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
        if (this.hasLoadingStateTarget) this.loadingStateTarget.classList.remove("hidden")
    }

    // --- Selection ---------------------------------------------------

    selectCountry(country) {
        this.applyCountryToUI(country)
        this.closeDropdown()
    }
}
